import React, { useState } from "react";
import { Plus } from "lucide-react";
import useAppData from "../../../customHooks/useAppData";
import { AttributeDefinition, AttributeValue, Department, ItemType, NewItemPayload } from "./types";
import {
    Block,
    Checkbox,
    CheckboxRow,
    CheckboxText,
    Col,
    DashedBox,
    DashedText,
    ErrorText,
    FieldLabel,
    FooterRow,
    FormCard,
    FormTitle,
    FormWrap,
    Grid,
    HelpText,
    HLine,
    Input,
    PrimaryButton,
    RequiredMark,
    SectionMini,
    SectionMiniLabel,
    Select,
} from "./styles";
import useUser from "../../../customHooks/useUser";

const todayIsoDate = () => new Date().toISOString().slice(0, 10);

interface AddItemViewProps {
    departments: Department[];
    itemTypes: ItemType[];
    defsForType: (typeId: number) => AttributeDefinition[];
    onAdd: (payload: NewItemPayload) => void;
}

export default function AddItemView({ departments, itemTypes, defsForType, onAdd }: AddItemViewProps) {
    const { user } = useUser();
    const unit = user.s_unit;

    const [deptId, setDeptId] = useState("");
    const [typeId, setTypeId] = useState("");
    const [name, setName] = useState("");
    const [quantity, setQuantity] = useState("");
    const [dateAdded, setDateAdded] = useState("");
    const [attrs, setAttrs] = useState<Record<string, AttributeValue>>({});
    const [errors, setErrors] = useState<Record<string, string>>({});

    const typesForDept = itemTypes.filter((t) => t.departmentId === Number(deptId));
    const defs = typeId ? defsForType(Number(typeId)) : [];

    const changeDept = (id: string) => {
        setDeptId(id);
        setTypeId("");
        setAttrs({});
        setErrors({});
    };

    const changeType = (id: string) => {
        setTypeId(id);
        setAttrs({});
        setErrors({});
    };

    const setAttr = (key: string, value: AttributeValue) => setAttrs((prev) => ({ ...prev, [key]: value }));

    const validate = (): boolean => {
        const e: Record<string, string> = {};
        if (!name.trim()) e.name = "Give the item a name.";
        const q = Number(quantity);
        if (quantity === "" || Number.isNaN(q) || q < 0) e.quantity = "Enter a quantity of zero or more.";
        if (!unit) e.unit = "Select a unit.";
        if (dateAdded && dateAdded > todayIsoDate()) e.dateAdded = "Date added can't be in the future.";
        defs.forEach((d) => {
            if (d.isRequired) {
                const v = attrs[d.attributeKey];
                if (v === undefined || v === "" || v === null) e[d.attributeKey] = `${d.label} is required.`;
            }
        });
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const submit = () => {
        if (!validate()) return;
        const cleanAttrs: Record<string, AttributeValue> = {};
        defs.forEach((d) => {
            let v = attrs[d.attributeKey];
            if (d.dataType === "bool") v = Boolean(v);
            if (v === undefined || v === "") return;
            cleanAttrs[d.attributeKey] = v;
        });
        onAdd({
            itemTypeId: Number(typeId),
            name: name.trim(),
            quantity: Number(quantity),
            unit,
            attributes: cleanAttrs,
            addedAt: dateAdded || undefined,
        });
    };

    return (
        <FormWrap>
            <FormCard>
                <FormTitle>Add an item</FormTitle>

                <Block mt={20}>
                    <Grid cols={2}>
                        <div>
                            <FieldLabel>Department</FieldLabel>
                            <Select value={deptId} onChange={(e) => changeDept(e.target.value)}>
                                <option value="">Select a department</option>
                                {departments.map((d) => (
                                    <option key={d.id} value={d.id}>
                                        {d.name}
                                    </option>
                                ))}
                            </Select>
                        </div>
                        <div>
                            <FieldLabel>Item type</FieldLabel>
                            <Select value={typeId} onChange={(e) => changeType(e.target.value)} disabled={!deptId}>
                                <option value="">{deptId ? "Select a type" : "Pick a department first"}</option>
                                {typesForDept.map((t) => (
                                    <option key={t.id} value={t.id}>
                                        {t.name}
                                    </option>
                                ))}
                            </Select>
                        </div>
                    </Grid>
                </Block>

                {!typeId ? (
                    <DashedBox>
                        <DashedText>Select a department/item type.</DashedText>
                    </DashedBox>
                ) : (
                    <>
                        <Block mt={24}>
                            <Grid cols={3}>
                                <Col span={2}>
                                    <FieldLabel>
                                        Name <RequiredMark>*</RequiredMark>
                                    </FieldLabel>
                                    <Input
                                        value={name}
                                        onChange={(e) => setName(e.target.value)}
                                        placeholder="e.g. Ramp jacket"
                                        error={!!errors.name}
                                    />
                                    {errors.name && <ErrorText>{errors.name}</ErrorText>}
                                </Col>
                                <div>
                                    <FieldLabel>
                                        Quantity <RequiredMark>*</RequiredMark>
                                    </FieldLabel>
                                    <Input
                                        type="number"
                                        min="0"
                                        value={quantity}
                                        onChange={(e) => setQuantity(e.target.value)}
                                        placeholder="0"
                                        mono
                                        error={!!errors.quantity}
                                    />
                                    {errors.quantity && <ErrorText>{errors.quantity}</ErrorText>}
                                </div>
                            </Grid>
                        </Block>

                        <Block mt={16}>
                            <Grid cols={2}>
                                <div>
                                    <FieldLabel>Date added</FieldLabel>
                                    <Input
                                        type="date"
                                        value={dateAdded}
                                        onChange={(e) => setDateAdded(e.target.value)}
                                        max={todayIsoDate()}
                                        error={!!errors.dateAdded}
                                    />
                                    {errors.dateAdded && <ErrorText>{errors.dateAdded}</ErrorText>}
                                </div>
                            </Grid>
                        </Block>

                        <Block mt={24}>
                            <SectionMini>
                                <SectionMiniLabel>{itemTypes.find((t) => t.id === Number(typeId))?.name} attributes</SectionMiniLabel>
                                <HLine />
                            </SectionMini>

                            {defs.length === 0 ? (
                                <HelpText>This type has no custom attributes.</HelpText>
                            ) : (
                                <Grid cols={2}>
                                    {defs.map((def) => (
                                        <AttributeField
                                            key={def.id}
                                            def={def}
                                            value={attrs[def.attributeKey]}
                                            onChange={(v) => setAttr(def.attributeKey, v)}
                                            error={errors[def.attributeKey]}
                                        />
                                    ))}
                                </Grid>
                            )}
                        </Block>

                        <FooterRow>
                            <PrimaryButton onClick={submit}>
                                <Plus size={16} />
                                Add item
                            </PrimaryButton>
                        </FooterRow>
                    </>
                )}
            </FormCard>
        </FormWrap>
    );
}

interface AttributeFieldProps {
    def: AttributeDefinition;
    value: AttributeValue | undefined;
    onChange: (value: AttributeValue) => void;
    error?: string;
}

function AttributeField({ def, value, onChange, error }: AttributeFieldProps) {
    if (def.dataType === "bool") {
        return (
            <CheckboxRow>
                <Checkbox type="checkbox" checked={Boolean(value)} onChange={(e) => onChange(e.target.checked)} />
                <CheckboxText>{def.label}</CheckboxText>
            </CheckboxRow>
        );
    }

    return (
        <div>
            <FieldLabel>
                {def.label} {def.isRequired && <RequiredMark>*</RequiredMark>}
            </FieldLabel>
            {def.dataType === "list" ? (
                <Select value={(value as string) ?? ""} onChange={(e) => onChange(e.target.value)} error={!!error}>
                    <option value="">Select {def.label.toLowerCase()}</option>
                    {def.options.map((o) => (
                        <option key={o} value={o}>
                            {o}
                        </option>
                    ))}
                </Select>
            ) : def.dataType === "number" ? (
                <Input type="number" value={(value as string) ?? ""} onChange={(e) => onChange(e.target.value)} mono error={!!error} />
            ) : (
                <Input
                    value={(value as string) ?? ""}
                    onChange={(e) => onChange(e.target.value)}
                    placeholder={`Enter ${def.label.toLowerCase()}`}
                    error={!!error}
                />
            )}
            {error && <ErrorText>{error}</ErrorText>}
        </div>
    );
}
